'use client';

import { useEffect } from 'react';

import { app } from '@/lib/bridge';

/**
 * 全局点按反馈：给「可点元素」补一次轻触觉。
 *
 * 照搬搬小书 `xhshub/apps/web/src/app/mobile/tap-feedback.tsx`。
 * 为什么放在全局而不是逐页加：原生侧每个按钮都有系统触感，WebView 里没有，
 * 用户会觉得「点了没反应、轻飘飘」。逐页加一定会漏，这里统一挂一次。
 *
 * 只在「按下 → 抬起」位移很小（= 点击，而不是滚动/滑动）时触发；40ms 内连点去抖。
 * 需要静默的元素或容器加 `data-haptic="off"`；页面里已自己发触感的地方
 * （如成功/失败）可以加这个属性避免叠两次。
 */
const INTERACTIVE = [
  'button',
  'a[href]',
  'label',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="radio"]',
  '[data-haptic]',
].join(',');

const MOVE_THRESHOLD = 10;
const DEBOUNCE_MS = 40;

export function TapFeedback() {
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let pending: Element | null = null;
    let lastAt = 0;

    const onPointerDown = (e: PointerEvent) => {
      const hit = (e.target as Element | null)?.closest?.(INTERACTIVE) ?? null;
      pending = hit && hit.closest('[data-haptic="off"]') === null ? hit : null;
      startX = e.clientX;
      startY = e.clientY;
    };

    const onPointerUp = (e: PointerEvent) => {
      const el = pending;
      pending = null;
      if (!el) return;
      // 位移大 = 滚动/滑动，不是点击
      if (
        Math.abs(e.clientX - startX) > MOVE_THRESHOLD ||
        Math.abs(e.clientY - startY) > MOVE_THRESHOLD
      ) {
        return;
      }
      const now = Date.now();
      if (now - lastAt < DEBOUNCE_MS) return;
      lastAt = now;
      void app.haptic('light').catch(() => null);
    };

    window.addEventListener('pointerdown', onPointerDown, { capture: true, passive: true });
    window.addEventListener('pointerup', onPointerUp, { capture: true, passive: true });
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, { capture: true });
      window.removeEventListener('pointerup', onPointerUp, { capture: true });
    };
  }, []);

  return null;
}
